import { Flex, Modal, Spin, Table as TableAntd } from 'antd';
import { Formik, FieldArray } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import Row from '~/components/Row';
import { createFinancialExpenses, updateFinancialExpenses } from '~/services/hooks/financialExpenses';
import FormControl from '~/components/Form/FormControl';
import { Input } from 'formik-antd';
import { SelectAntd } from '~/components/Pagination/styles';
import * as Yup from 'yup';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { Table } from '~/components/Table';
import { financialExpenseItemsColumns } from '~/tables/financialExpenseItems';
import Button from '~/components/Button';
import { FaPlus } from 'react-icons/fa';
import errorHandler from '~/Utils/errorHandler';

const initial = {
  name: '',
  expense_type: 0,
  financial_expense_items: [],
};

export default function ExpenseModal({ visible, onClose, expense, isMobile }) {
  const { t } = useTranslation();
  const authCheck = useCheckAuth();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(initial);

  const saveFinancialExpense = async (values, { setErrors }) => {
    setLoading(true);
    try {
      if (expense) {
        await updateFinancialExpenses(expense.id, values);
      } else {
        await createFinancialExpenses(values);
      }
      onClose();
    } catch (error) {
      setErrors(errorHandler(error));
    }
    setLoading(false);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Nome é obrigatório'),
    expense_type: Yup.number().required('Tipo de despesa é obrigatório'),
    financial_expense_items: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required('Nome do item de despesa é obrigatório'),
        })
      )
      .nullable(),
  });

  const fluidPermissions = () => {
    if (expense) {
      return authCheck('@expenses/edit');
    } else {
      return authCheck('@expenses/create');
    }
  };

  useEffect(() => {
    if (visible) {
      if (expense) {
        setRecord(expense);
      } else {
        setRecord(initial);
      }
    }
  }, [visible]);

  return (
    <Formik
      onSubmit={saveFinancialExpense}
      validationSchema={validationSchema}
      initialValues={record}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {({ isSubmitting, submitForm, resetForm, errors, values, setFieldValue }) => (
        <Modal
          title={(expense ? t('screens:expenses.editTitle') : t('screens:expenses.newTitle')) + values.name}
          onCancel={onClose}
          style={{ maxWidth: isMobile ? '100%' : '700px' }}
          afterClose={resetForm}
          open={visible}
          loading={isSubmitting}
          centered
          width={isMobile ? '400px' : '700px'}
          footer={<ModalFooter onOk={submitForm} loading={loading || isSubmitting} onCancel={onClose} />}
        >
          <Spin spinning={loading}>
            <Row>
              <FormControl
                cols={{ xs: 16 }}
                field="name"
                error={errors?.name}
                required
                label={t('fields:expenses.name')}
              >
                <Input name="name" disabled={!fluidPermissions()} />
              </FormControl>
              <FormControl
                cols={{ xs: 8 }}
                field="expense_type"
                error={errors?.expense_type}
                required
                label={t('fields:expenses.type')}
              >
                <SelectAntd
                  placeholder="SELECIONE"
                  value={values?.expense_type}
                  onSelect={(value) => setFieldValue('expense_type', value)}
                  loading={loading}
                  disabled={!fluidPermissions()}
                >
                  <SelectAntd.Option key={0} value={0}>
                    {t('screens:expenses.fixed')}
                  </SelectAntd.Option>
                  <SelectAntd.Option key={1} value={1}>
                    {t('screens:expenses.fixedVariable')}
                  </SelectAntd.Option>
                  <SelectAntd.Option key={2} value={2}>
                    {t('screens:expenses.variable')}
                  </SelectAntd.Option>
                </SelectAntd>
              </FormControl>
            </Row>
            <FieldArray name="financial_expense_items">
              {({ push, remove }) => (
                <Table
                  dataSource={values?.financial_expense_items?.map((fei, i) => ({
                    ...fei,
                    key: i,
                    remove,
                  }))}
                  columns={financialExpenseItemsColumns(errors, fluidPermissions)}
                  size="small"
                  rowClassName={() => 'editable-row'}
                  bordered
                  empty={false}
                  summary={() => {
                    return (
                      <TableAntd.Summary.Row>
                        <TableAntd.Summary.Cell>
                          <Flex style={{ height: '100%' }} align="flex-start" justify="center">
                            <Button
                              color="primary"
                              title="Adicionar despesa"
                              size="small"
                              onClick={() =>
                                push({
                                  name: null,
                                })
                              }
                              style={{ width: '30px' }}
                              disabled={!fluidPermissions()}
                            >
                              <FaPlus />
                            </Button>
                          </Flex>
                        </TableAntd.Summary.Cell>
                      </TableAntd.Summary.Row>
                    );
                  }}
                />
              )}
            </FieldArray>
          </Spin>
        </Modal>
      )}
    </Formik>
  );
}
